import { AppToasterV2, dialogStore } from "@ucl/library";
import { useCallback, useEffect, useState } from "react";
import { KeyPerson, KeyPersonApplicationRoleMetricsDTO } from "@/types/KeyPerson";
import { useAuthenticatedApiClient } from "./useAuthenticatedApiClient";
import KeyPersonApiClient from "@/api/keyPersonApiClient";
import { FinancialApplicationSection } from "@/types/enums";
import {
  FinancialApplicationFormValidationDisplayBehavior,
  useFinancialApplicationFormSectionValidation,
  FinancialApplicationFormSectionContextType,
} from "./useFinancialApplicationFormSectionValidation";
import { useFinancialApplicationForm } from "./useFinancialApplicationForm";

export const useKeyPeople = (
  financialApplicationId: string,
  validationBehavior?: FinancialApplicationFormValidationDisplayBehavior,
  validationContextType?: FinancialApplicationFormSectionContextType
) => {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isCreating, setIsCreating] = useState<boolean>(false);
  const [keyPersonIds, setKeyPersonIds] = useState<string[]>([]);
  const [keyPeopleMetrics, setKeyPeopleMetrics] = useState<KeyPerson[] | null>(null);
  const [metrics, setMetrics] = useState<KeyPersonApplicationRoleMetricsDTO | null>(null);

  const keyPersonApiClient = useAuthenticatedApiClient(KeyPersonApiClient);

  const { refreshFinancialApplication } = useFinancialApplicationForm(financialApplicationId);
  const { getCollectionValidationErrors, validateFinancialApplicationFormSection } =
    useFinancialApplicationFormSectionValidation(
      financialApplicationId,
      FinancialApplicationSection.KeyPeople,
      validationBehavior,
      validationContextType
    );

  const validationErrors = getCollectionValidationErrors("KeyPeople");

  useEffect(() => {
    getKeyPersonIds();
  }, [financialApplicationId]);

  useEffect(() => {
    if (keyPersonIds.length > 0) {
      getKeyPeopleMetrics();
    }
  }, [keyPersonIds]);

  const getKeyPersonIds = async () => {
    setIsLoading(true);
    keyPersonApiClient
      .getKeyPersonIds(financialApplicationId)
      .then((ids) => {
        setKeyPersonIds(ids);
      })
      .catch((error) => {
        console.error(error);
        AppToasterV2.showFailedActionToaster("Failed to get key person IDs.");
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const getKeyPeopleMetrics = async (value?: KeyPerson) => {
    if (!value) {
      keyPersonApiClient
        .getKeyPeopleApplicationRoleMetrics(financialApplicationId)
        .then((metricsData) => {
          setMetrics(metricsData);
        })
        .catch((error) => {
          console.error(error);
          AppToasterV2.showFailedActionToaster("Failed to get key people metrics.");
        });
    } else {
      setKeyPeopleMetrics((prevMetrics) => {
        if (!prevMetrics) {
          return [value];
        }

        // Check if a KeyPerson with the same ID already exists
        const existingIndex = prevMetrics.findIndex((person) => person.id === value.id);

        if (existingIndex !== -1) {
          // Replace existing KeyPerson
          const updatedMetrics = [...prevMetrics];
          updatedMetrics[existingIndex] = value;
          return updatedMetrics;
        } else {
          // Add new KeyPerson
          return [...prevMetrics, value];
        }
      });

      // Calculate metrics based on current keyPeopleMetrics
      setKeyPeopleMetrics((currentMetrics) => {
        if (!currentMetrics || currentMetrics.length === 0) {
          setMetrics({
            beneficialOwnerCount: 0,
            authorizedSignerCount: 0,
            controlPersonCount: 0,
          });
          return currentMetrics;
        }

        const calculatedMetrics = currentMetrics.reduce(
          (acc, person) => ({
            beneficialOwnerCount: acc.beneficialOwnerCount + (person.isBeneficialOwner ? 1 : 0),
            authorizedSignerCount: acc.authorizedSignerCount + (person.isAuthorizedSigner ? 1 : 0),
            controlPersonCount: acc.controlPersonCount + (person.isControlPerson ? 1 : 0),
          }),
          {
            beneficialOwnerCount: 0,
            authorizedSignerCount: 0,
            controlPersonCount: 0,
          }
        );

        setMetrics(calculatedMetrics);
        return currentMetrics;
      });
    }
  };

  const handleDeleteKeyPerson = async (keyPersonId: string) => {
    dialogStore.openConfirmDialog({
      dialogId: "delete-key-person-" + keyPersonId,
      headerText: "Delete Key Person",
      content: "Are you sure you want to delete this key person?",
      confirmButtonText: "Delete",
      loadingProgressContent: "Delete",
      loadingSuccessContent: "Delete",
      color: "sunset-red",
      onConfirm: async () => {
        const result = {
          isSuccess: true,
          error: undefined,
        };

        try {
          await keyPersonApiClient.deleteKeyPerson(financialApplicationId, keyPersonId);
          setKeyPersonIds(keyPersonIds.filter((id) => id !== keyPersonId));
          dialogStore.closeDialog("delete-key-person-" + keyPersonId);
          AppToasterV2.showSuccessAppToaster("Key person deleted.");
          await refreshFinancialApplication();
          getKeyPeopleMetrics();
        } catch (error) {
          console.error(error);
          result.isSuccess = false;
        }

        return result;
      },
    });
  };

  const handleAddKeyPerson = async () => {
    try {
      setIsCreating(true);
      const keyPerson = await keyPersonApiClient.createKeyPerson(financialApplicationId);
      if (keyPerson.id) {
        setKeyPersonIds([keyPerson.id, ...keyPersonIds]);
        AppToasterV2.showSuccessAppToaster("Key person added.");
        await refreshFinancialApplication();
      }
    } catch (error) {
      console.error(error);
      AppToasterV2.showFailedActionToaster("Failed to add key person.");
    } finally {
      setIsCreating(false);
    }
  };

  const handleApplicableRolesChange = (value: KeyPerson) => {
    getKeyPeopleMetrics(value);
  };

  const validateKeyPeople = useCallback(async (): Promise<boolean> => {
    const { isValid } = await validateFinancialApplicationFormSection();
    return isValid;
  }, [validateFinancialApplicationFormSection]);

  return {
    keyPersonIds,
    keyPeopleMetrics,
    handleDeleteKeyPerson,
    handleAddKeyPerson,
    validateKeyPeople,
    isLoading,
    isCreating,
    validationErrors,
    metrics,
    handleApplicableRolesChange,
  };
};
